import React, { useState, useEffect, useRef, useMemo } from "react";
import ReactDOM from "react-dom";
import CommentsBlock from "./CommentsBlock";
import WriteComment from "./WriteComment";
import momentTz from "moment-timezone";
import searchIcon from "../../assets/search.svg";
import searchBlackIcon from "../../assets/search-black.svg";
import { ReactComponent as SortIcon } from "../../assets/sort-icon-color.svg";
import downloadIcon from "../../assets/download.svg";
import { ReactComponent as CopyIcon } from "../../assets/CopyIcon.svg";
import AddCommentModal from "./AddCommentModal";
import { uniqBy } from "lodash";
import Avatar from "../Avatar/Avatar";
import SortMenu from "./SortMenu";
import { useTranslation } from "react-i18next";
import "./CommentsLayout.css";
import plusIcon from "../../assets/plus.svg";
import closeIcon from "../../assets/clear.svg";
import { useDispatch, useSelector } from "react-redux";
import {
  currentOrganizationSelector,
  organizationUsersSelector,
  userSelector,
} from "../../store/selectors";
import moment from "moment";
import { fetchTags } from "../../store/projectsSlice";
import toast from "react-hot-toast";
import useIsMobile from "../../hooks/useIsMobile";

const colorsArray = [
  "#9D7BFF",
  "#EA5B6F",
  "#FF78B5",
  "#C64CBF",
  "#B69BF0",
  "#F49BAB",
  "#D23D9B",
  "#CA6DC1",
  "#6B97FD",
  "#5070FF",
  "#3386FF",
  "#FFBA5F",
  "#E98D35",
  "#FD895F",
  "#FAFA92",
  "#FEFFC1",
  "#FFCD67",
  "#AFC55E",
  "#3EDD95",
  "#88D669",
  "#87DDFF",
  "#2BC1DF",
  "#17B7AC",
  "#93DA97",
];

// const CURRENT_USER = {
//   id: 1,
//   name: "Current User",
// };

const CommentsLayout = ({
  newCommentEnd = null,
  setNewCommentEnd = () => {},
  setNewMarkerInfo,
  newMarkerInfo,
  currentTime,
  myProjectRole,
  videoRef,
  comments,
  isSendingComment,
  videoControls,
  versionId,
  onCommentsChange,
  markerPoint,
  onSetMarkerPoint,
  isLocationMode,
  setIsLocationMode,
  updateComments,
  onPressTimestamp,
  onSubmitComment,
  onDeleteComment,
  onRestoreComment,
  onForceDeleteComment,
  commentsFilter,
  onChangeCommentsFilter,
  isShowUnreplied,
  setIsShowUnreplied,
  projectUsers,
  tagsArray,
  setTagsArray,
  highlightCommentId,
  filteredTags,
  currentHoveredCommentId,
  setCurrentHoveredCommentId,
  onShowSharedNeedLogin,
  isShared,
  isViewer,
}) => {
  const { t } = useTranslation();
  // const [isModalOpen, setIsModalOpen] = useState(false);
  const [showOnlyUserComments, setShowOnlyUserComments] = useState(false);
  const currentOrganization = useSelector(currentOrganizationSelector);
  const dispatch = useDispatch();
  const user = useSelector(userSelector);
  const isMobile = useIsMobile();
  // State to control popup for tag filter row hover and its position (for Portal)
  const [showTagFilterPopup, setShowTagFilterPopup] = useState(false);
  const [popupCoords, setPopupCoords] = useState({ left: 0, top: 0 });
  const tagFilterRowRef = useRef(null);
  const organizationUsers = useSelector(organizationUsersSelector);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);
  const [sortOrder, setSortOrder] = useState("timecode");
  const [selectedTag, setSelectedTag] = useState({ name: "Все", id: -1 });
  const sortButtonRef = useRef(null);
  const [existingTagsIds, setExistingTagsIds] = useState([]);
  const [tagsDefaultColors, setTagsDefaultColors] = useState({});
  const noTags = filteredTags.length === 0;
  const [currentEditedComment, setCurrentEditedComment] = useState(null);
  const [lastUsedTags, setLastUsedTags] = useState([]);

  useEffect(() => {
    handleUpdateTags();
  }, []);

  useEffect(() => {
    handleUpdateTags();
  }, [comments]);

  const tagSort = (a, b) => {
    const hasColorA = a.color !== null;
    const hasColorB = b.color !== null;

    const isLastUsedA = lastUsedTags.indexOf(a.id) > -1;
    const isLastUsedB = lastUsedTags.indexOf(b.id) > -1;

    if (isLastUsedA && !isLastUsedB) return -1; // a first
    if (!isLastUsedA && isLastUsedB) return 1; // b first

    if (isLastUsedA && isLastUsedB) {
      return lastUsedTags.indexOf(a.id) < lastUsedTags.indexOf(b.id) ? -1 : 1;
    }

    if (hasColorA && !hasColorB) return -1; // a first
    if (!hasColorA && hasColorB) return 1; // b first

    // both colored → sort by color alphabetically
    if (hasColorA && hasColorB) {
      return a > b ? -1 : 1;
    }

    // both uncolored → sort by name alphabetically
    return a.name.localeCompare(b.name);
  };

  const formatTimecode = (timecode) => {
    if (!timecode.includes("-")) {
      let numberOfMinutes = Math.floor(+timecode / 60);
      let numberOfSeconds = Math.floor(+timecode - 60 * numberOfMinutes);

      if (numberOfMinutes < 10)
        numberOfMinutes = "0" + numberOfMinutes.toString();
      if (numberOfSeconds < 10)
        numberOfSeconds = "0" + numberOfSeconds.toString();
      return `${numberOfMinutes}:${numberOfSeconds}`;
    }

    const [timeRangeStart, timeRangeEnd] = timecode.split(" - ");

    let numberOfMinutesStart = Math.floor(+timeRangeStart / 60);
    let numberOfSecondsStart = Math.floor(
      +timeRangeStart - 60 * numberOfMinutesStart
    );
    if (numberOfMinutesStart < 10)
      numberOfMinutesStart = "0" + numberOfMinutesStart.toString();
    if (numberOfSecondsStart < 10)
      numberOfSecondsStart = "0" + numberOfSecondsStart.toString();

    let numberOfMinutesEnd = Math.floor(+timeRangeEnd / 60);
    let numberOfSecondsEnd = Math.floor(
      +timeRangeEnd - 60 * numberOfMinutesEnd
    );
    if (numberOfMinutesEnd < 10)
      numberOfMinutesEnd = "0" + numberOfMinutesEnd.toString();
    if (numberOfSecondsStart < 10)
      numberOfSecondsEnd = "0" + numberOfSecondsEnd.toString();

    return `${numberOfMinutesStart}:${numberOfSecondsStart} - ${numberOfMinutesEnd}:${numberOfSecondsEnd}`;

    return timecode;
  };

  const onPressCopyComments = () => {
    const commentsText = comments.reduce((res, comment) => {
      const commentText = `${comment.author.firstName} ${
        comment.author.lastName
      } (${formatDate(comment.createdDate)})\n[${formatTimecode(
        comment.timecode
      )}] ${comment.content}`;
      res += commentText + "\n\n";
      return res;
    }, "");
    navigator.clipboard.writeText(commentsText);
    toast.success("Комментарии скопированы в буфер обмена");
  };

  const handleUpdateTags = async () => {
    let defaultColors = JSON.parse(localStorage.getItem("tags_colors") || "{}");
    const lastUsedTagIds =
      JSON.parse(localStorage.getItem("lastUsedTagIds")) || [];
    setLastUsedTags(lastUsedTagIds);
    if (!currentOrganization) return;
    dispatch(fetchTags(currentOrganization.id)).then((res) => {
      if (!res.error) {
        const uniqueArray = uniqBy(res.payload, "name");
        const sortedArray = uniqueArray.sort(tagSort);
        const resTags = sortedArray.map((tag) => {
          if (tag.color) return tag;
          if (!!defaultColors[tag.id]) {
            return {
              ...tag,
              color: defaultColors[tag.id],
            };
          } else {
            let color =
              colorsArray[Math.floor(Math.random() * colorsArray.length)];
            defaultColors[tag.id] = color;
            return {
              ...tag,
              color,
            };
          }
        });
        setTagsArray(resTags);
        setTagsDefaultColors(defaultColors);
        localStorage.setItem("tags_colors", JSON.stringify(defaultColors));
      }
    });
  };

  // Extract unique tags from status field
  const uniqueTags = useMemo(() => {
    const tags = new Set();
    comments.forEach((c) => {
      if (c.status) tags.add(c.status);
    });
    return Array.from(tags);
  }, [comments]);

  const handleArchive = (commentId) => {
    handleDeleteComment(commentId);
  };

  const handleRestore = (commentId) => {
    handleRestoreComment(commentId);
  };

  const handleDeleteComment = (commentId) => {
    onDeleteComment(commentId);
  };

  const handleForceDeleteComment = async (commentId) => {
    return await onForceDeleteComment(commentId);
  };

  const handleRestoreComment = (commentId) => {
    onRestoreComment(commentId);
  };

  const handleSortChange = (newSortOrder) => {
    setSortOrder(newSortOrder);
    setIsSortMenuOpen(false);
  };

  const handleFilterChange = (newFilterType) => {
    onChangeCommentsFilter(newFilterType);
    setIsSortMenuOpen(false);
  };

  // const handleAddComment = (newComment) => {
  //   onCommentsChange((prevComments) => [
  //     {
  //       id: Date.now(),
  //       userId: CURRENT_USER.id,
  //       author: CURRENT_USER.name,
  //       time: new Date().toLocaleTimeString("ru-RU", {
  //         hour: "2-digit",
  //         minute: "2-digit",
  //       }),
  //       timestamp: newComment.timeRange.end
  //         ? `${newComment.timeRange.start} - ${newComment.timeRange.end}`
  //         : newComment.timeRange.start,
  //       content: newComment.content,
  //       status: "editing",
  //       statusColor: "#ff5c38",
  //       ...newComment,
  //     },
  //     ...prevComments,
  //   ]);
  // };

  const filteredComments = comments
    .filter((comment) => {
      let myCommentsFilter = true;
      if (showOnlyUserComments) {
        myCommentsFilter = !user || comment.author.id === user.userId;
      }

      let searchFilter = true;
      if (isSearchActive && searchQuery) {
        const searchLower = searchQuery.toLowerCase();

        searchFilter =
          comment.content.toLowerCase().includes(searchLower) ||
          comment.author.toLowerCase().includes(searchLower);
      }

      let tagsFilter = true;
      if (selectedTag.name !== "Все") {
        const tagIndex = comment.tags.findIndex(
          (commentTag) => commentTag.name === selectedTag.name
        );

        tagsFilter = tagIndex > -1;
      }

      let unrepliedFilter = true;
      if (isShowUnreplied) {
        unrepliedFilter = comment.repliesCount === 0;
      }

      return myCommentsFilter && searchFilter && tagsFilter && unrepliedFilter;
    })
    .sort((a, b) => {
      const isBefore = moment.utc(a).isBefore(moment.utc(b));
      const isTimecodeBefore = +a.timecode > +b.timecode;
      if (sortOrder === "new") {
        return isBefore ? 1 : -1;
      } else if (sortOrder === "timecode") {
        return isTimecodeBefore ? 1 : -1;
      } else {
        return isBefore ? -1 : 1;
      }
    });

  const getSortMenuPosition = () => {
    if (!sortButtonRef.current) return {};
    const rect = sortButtonRef.current.getBoundingClientRect();
    return {
      top: rect.bottom + 8,
      right: window.innerWidth - rect.right,
    };
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const res = momentTz(dateString).tz("etc/gmt-6").format("DD.MM.YYYY HH:mm");
    return res;
  };

  const neededTagsArray = filteredTags.length ? filteredTags : tagsArray;

  return (
    <div className="comments-layout">
      <div className="comments-header">
        <span className={`comments-title ${isSearchActive ? "hidden" : ""}`}>
          {t("comments.title")}
        </span>
        <div className="header-buttons">
          <div
            style={{ marginRight: 38, marginTop: 1, cursor: "pointer" }}
            onClick={() => {
              onPressCopyComments();
            }}
          >
            <CopyIcon
              style={{
                color: "#717b8e",
                transform: "scale(1.3)",
              }}
            />
          </div>
          <div
            className={`search-container ${
              isSearchActive ? "search-active" : ""
            }`}
          >
            <input
              type="text"
              className="search-input"
              placeholder={t("comments.search")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setIsSearchActive(true)}
              onBlur={() => {
                if (!searchQuery) setIsSearchActive(false);
              }}
            />
            {searchQuery && (
              <button
                className="clear-search-btn"
                onClick={() => {
                  setSearchQuery("");
                  setTimeout(
                    () => document.querySelector(".search-input")?.focus(),
                    0
                  );
                }}
                tabIndex={-1}
              >
                <img src={closeIcon} alt="clear" />
              </button>
            )}

            <button
              className={`search-icon-btn${isSearchActive ? " active" : ""}`}
              tabIndex={-1}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                setIsSearchActive(true);
                setTimeout(
                  () => document.querySelector(".search-input")?.focus(),
                  0
                );
              }}
            >
              <img
                src={isSearchActive ? searchBlackIcon : searchIcon}
                alt="search"
                className="search-icon-img"
              />
            </button>
          </div>
          <div
            ref={sortButtonRef}
            className="sort-icon-btn"
            style={{
              width: 32,
              height: 32,
              marginLeft: 10,
              backgroundColor: "transparent",
              boxShadow: "none",
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              justifyContent: "center",
              position: "relative",
            }}
            onClick={() => setIsSortMenuOpen(!isSortMenuOpen)}
          >
            <SortIcon />
          </div>
        </div>
      </div>
      {!isViewer && isMobile && (
        <WriteComment
          setNewMarkerInfo={setNewMarkerInfo}
          newCommentEnd={newCommentEnd}
          newMarkerInfo={newMarkerInfo}
          setNewCommentEnd={setNewCommentEnd}
          versionId={versionId}
          isShared={isShared}
          tagsDefaultColors={tagsDefaultColors}
          currentTime={currentTime}
          isSendingComment={isSendingComment}
          // onSubmit={handleAddComment}
          videoRef={videoRef}
          videoControls={videoControls}
          markerPoint={markerPoint}
          onSetMarkerPoint={onSetMarkerPoint}
          isLocationMode={isLocationMode}
          setIsLocationMode={setIsLocationMode}
          onSubmitComment={onSubmitComment}
          updateTagsArray={handleUpdateTags}
          projectUsers={projectUsers}
          tagsArray={tagsArray}
          currentEditedComment={currentEditedComment}
          resetCurrentEditedComment={() => setCurrentEditedComment(null)}
          onShowSharedNeedLogin={onShowSharedNeedLogin}
        />
      )}
      <SortMenu
        isOpen={isSortMenuOpen}
        onClose={() => setIsSortMenuOpen(false)}
        sortOrder={sortOrder}
        onSortChange={handleSortChange}
        filterType={commentsFilter}
        onFilterChange={handleFilterChange}
        isShowUnreplied={isShowUnreplied}
        setIsShowUnreplied={setIsShowUnreplied}
        position={getSortMenuPosition()}
      />
      {!isMobile ||
        (!!comments.length && (
          <>
            <div className="filter-container">
              <button
                className={`filter-button filter-button-left ${
                  !showOnlyUserComments
                    ? "filter-button--active"
                    : "filter-button--inactive"
                }`}
                onClick={() => setShowOnlyUserComments(false)}
              >
                <span className="filter-text">{t("comments.filters.all")}</span>
                <span className="filter-count">{comments.length}</span>
              </button>
              <button
                className={`filter-button filter-button-right ${
                  showOnlyUserComments
                    ? "filter-button--active"
                    : "filter-button--inactive"
                }`}
                onClick={() => setShowOnlyUserComments(true)}
              >
                {user && <Avatar user={user} name={user.email} size={24} />}

                <span className="filter-text">{t("comments.filters.my")}</span>
              </button>
            </div>
            {/* Tag filter row */}

            <div
              className={noTags ? "tag-filter-row-notags" : "tag-filter-row"}
              style={{}}
              ref={tagFilterRowRef}
              onMouseEnter={() => {
                if (tagFilterRowRef.current) {
                  const rect = tagFilterRowRef.current.getBoundingClientRect();
                  setPopupCoords({
                    left: rect.left + window.scrollX + 70,
                    top: rect.bottom + window.scrollY - 5, // 6px gap
                  });
                }
                setShowTagFilterPopup(true);
              }}
              onMouseLeave={() => setShowTagFilterPopup(false)}
            >
              <button
                className={
                  noTags
                    ? "tag-filter-pill-notags"
                    : `tag-filter-pill${
                        selectedTag.name === "Все" ? " active" : ""
                      }`
                }
                onClick={() => setSelectedTag({ name: "Все", id: -1 })}
              >
                Все
              </button>
              {neededTagsArray.map((tag) => (
                <button
                  key={"tag_" + tag.id}
                  className={
                    noTags ? "tag-filter-pill-notags" : `tag-filter-pill`
                  }
                  style={{
                    backgroundColor:
                      selectedTag.name === tag.name
                        ? `${selectedTag.color}10`
                        : "",
                    border:
                      selectedTag.name === tag.name
                        ? `1px solid ${selectedTag.color}80`
                        : "1px solid transparent",
                    color: noTags
                      ? "#717b8e70"
                      : selectedTag.name === tag.name
                      ? `${selectedTag.color}`
                      : "#717b8e",
                  }}
                  onClick={() => {
                    if (noTags) return;
                    setSelectedTag(tag);
                  }}
                >
                  {tag.name}
                </button>
              ))}
              {/* Popup on hover for no tags */}
              {/* Portal-based popup so it's never covered */}
              {showTagFilterPopup &&
                noTags &&
                ReactDOM.createPortal(
                  <div
                    className="tag-filter-popup"
                    style={{
                      position: "absolute",
                      left: `${popupCoords.left}px`,
                      top: `${popupCoords.top}px`,
                      width: "207px",
                      zIndex: 2000,
                      background: "#333B4B",
                      color: "#fff",
                      padding: "8px 16px",
                      borderRadius: "8px",
                      fontSize: "14px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.09)",
                      pointerEvents: "none",
                      overflowWrap: "break-word",
                      boxSizing: "border-box",
                      whiteSpace: "normal",
                    }}
                  >
                    Добавляйте теги при создании комментариев для вашего
                    удобства
                  </div>,
                  document.body
                )}
            </div>
          </>
        ))}
      <div className="comments-scroll-container">
        <CommentsBlock
          myProjectRole={myProjectRole}
          isViewer={isViewer}
          onShowSharedNeedLogin={onShowSharedNeedLogin}
          isShared={isShared}
          updateComments={updateComments}
          isSendingComment={isSendingComment}
          videoControls={videoControls}
          commentsArray={filteredComments}
          commentsFilter={commentsFilter}
          onPressTimestamp={onPressTimestamp}
          onArchive={handleArchive}
          onRestore={handleRestore}
          onDelete={handleDeleteComment}
          onForceDelete={handleForceDeleteComment}
          videoRef={videoRef}
          versionId={versionId}
          highlightCommentId={highlightCommentId} // pass down
          tagsDefaultColors={tagsDefaultColors}
          selectedTag={selectedTag}
          setSelectedTag={setSelectedTag}
          onSetEditedComment={(comment) => {
            setCurrentEditedComment(comment);
            document.querySelector("textarea.comment-input")?.focus();
          }}
          setCurrentHoveredCommentId={setCurrentHoveredCommentId}
          currentHoveredCommentId={currentHoveredCommentId}
        />
      </div>
      {!isViewer &&
        !isMobile &&
        (!myProjectRole || myProjectRole.role !== "VIEWER") && (
          <WriteComment
            setNewMarkerInfo={setNewMarkerInfo}
            newCommentEnd={newCommentEnd}
            newMarkerInfo={newMarkerInfo}
            setNewCommentEnd={setNewCommentEnd}
            versionId={versionId}
            isShared={isShared}
            tagsDefaultColors={tagsDefaultColors}
            currentTime={currentTime}
            isSendingComment={isSendingComment}
            // onSubmit={handleAddComment}
            videoRef={videoRef}
            videoControls={videoControls}
            markerPoint={markerPoint}
            onSetMarkerPoint={onSetMarkerPoint}
            isLocationMode={isLocationMode}
            setIsLocationMode={setIsLocationMode}
            onSubmitComment={onSubmitComment}
            updateTagsArray={handleUpdateTags}
            projectUsers={projectUsers}
            tagsArray={tagsArray}
            currentEditedComment={currentEditedComment}
            resetCurrentEditedComment={() => setCurrentEditedComment(null)}
            onShowSharedNeedLogin={onShowSharedNeedLogin}
          />
        )}
    </div>
  );
};

export default CommentsLayout;
